import { useCallback, useContext } from 'react';

import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  InputGroup,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { Formik } from 'formik';
import { MdOutlineLockClock } from 'react-icons/md';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { InferType } from 'yup';

import { DealStateEnum } from '../../gql/dealGql';
import { useCreateAndSendSecureLinkMutation } from '../../gql/generated/graphql';

import Modal from '../shared/Modal';
import Select from '../shared/Select';
import Tooltip from '../shared/Tooltip';

import { LDFlags } from '../../constants/experiments';
import { SECURE_LINK_EXPIRATION_OPTIONS } from '../../constants/secureLink';
import useFlag from '../../hooks/useFlag';
import { DealContext } from '../../libs/DealContext';
import { RemoveIndex } from '../../utils/types';

const validationSchema = Yup.object().shape({
  expiresInHours: Yup.string()
    .optional()
    .nullable()
    .oneOf(
      SECURE_LINK_EXPIRATION_OPTIONS.map((option) => option.value),
      'Link Expiration is required',
    )
    .required('Link Expiration is required'),
  sendText: Yup.boolean(),
  sendEmail: Yup.boolean().test(
    'at-least-one-method',
    'At least one method is required',
    (sendEmail, { parent }) => {
      const { sendText } = parent;
      return sendText || sendEmail;
    },
  ),
});

export type SendSecureLinkFormValues = RemoveIndex<InferType<typeof validationSchema>>;

const SendSecureLinkModalButton = () => {
  const { deal } = useContext(DealContext);
  const secureInfoLinkEnabled = useFlag(LDFlags.SECURE_INFO_LINK);

  const styles = useMultiStyleConfig('FormControl');

  const { isOpen, onOpen, onClose } = useDisclosure();

  const canShowSecureLink = secureInfoLinkEnabled && deal.state === DealStateEnum.SoftClose;

  const [createAndSendSecureLink, { loading: loadingCreateAndSendSecureLink }] =
    useCreateAndSendSecureLinkMutation();

  const initialValues: SendSecureLinkFormValues = {
    expiresInHours: '',
    sendText: false,
    sendEmail: false,
  };

  const handleSubmit = useCallback(
    async (values: SendSecureLinkFormValues) => {
      if (!deal.customer.id) {
        toast.error('Customer not found. Please send manually');
        return;
      }

      try {
        const response = await createAndSendSecureLink({
          variables: {
            customerId: deal.customer.id,
            expiresInHours: Number(values.expiresInHours),
            sendText: !!values.sendText,
            sendEmail: !!values.sendEmail,
          },
        });
        if (!response.data?.createAndSendSecureLink?.success) {
          throw new Error();
        }

        if (values.sendText) {
          if (response.data?.createAndSendSecureLink?.textError) {
            toast.error(
              `Text message failed to send: ${response.data.createAndSendSecureLink.textError}`,
            );
          } else {
            toast.success('Text message sent successfully');
          }
        }
        if (values.sendEmail) {
          if (response.data?.createAndSendSecureLink?.emailError) {
            toast.error(
              `Email failed to send: ${response.data.createAndSendSecureLink.emailError}`,
            );
          } else {
            toast.success('Email sent successfully');
          }
        }

        onClose();
      } catch (error) {
        toast.error('Failed to create and send secure link');
      }
    },
    [deal.customer.id, createAndSendSecureLink, onClose],
  );

  if (!canShowSecureLink) {
    return null;
  }

  return (
    <>
      <Button
        variant="secondary"
        size="smWithIconLeft"
        leftIcon={<MdOutlineLockClock fontSize={16} />}
        onClick={onOpen}
      >
        Send Secure Link
      </Button>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
        enableReinitialize
      >
        {({ values, errors, touched, isValid, handleChange, handleBlur }) => (
          <Modal
            isOpen={isOpen}
            onClose={onClose}
            canDismiss={!loadingCreateAndSendSecureLink}
            isCentered
            hasForm
            title="Send Customer Entry Link"
            leftButtons={
              <Button
                variant="warning"
                onClick={onClose}
                isDisabled={loadingCreateAndSendSecureLink}
              >
                Cancel
              </Button>
            }
            rightButtons={
              <Tooltip errors={errors}>
                <Button
                  type="submit"
                  isDisabled={!isValid}
                  isLoading={loadingCreateAndSendSecureLink}
                  loadingText="Send Link"
                >
                  Send Link
                </Button>
              </Tooltip>
            }
          >
            <VStack align="flex-start" gap={2}>
              <Text fontSize="sm">
                Unique link will be generated and sent to customer to complete the remaining fields
                of the credit application. Link will expire and be inaccessible after time selected
                below. Deal will be locked while customer link is active.
              </Text>

              <Select
                name="expiresInHours"
                label="Link Expiration"
                options={SECURE_LINK_EXPIRATION_OPTIONS}
                value={values.expiresInHours}
              />

              <FormControl
                isInvalid={(touched.sendText || touched.sendEmail) && !!errors.sendEmail}
                sx={styles.control}
              >
                <FormLabel sx={styles.label}>Send via:</FormLabel>
                <InputGroup sx={styles.input}>
                  <Stack>
                    <Checkbox
                      name="sendText"
                      isChecked={values.sendText}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isDisabled={!deal.customer.phone_number}
                      fontSize="sm"
                    >
                      <Text fontSize="sm">Text {deal.customer.phone_number || ''}</Text>
                    </Checkbox>
                    <Checkbox
                      name="sendEmail"
                      isChecked={values.sendEmail}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isDisabled={!deal.customer.email}
                      fontSize="sm"
                    >
                      <Text fontSize="sm">Email {deal.customer.email ?? ''}</Text>
                    </Checkbox>
                  </Stack>
                </InputGroup>
                <FormErrorMessage sx={styles.error}>{errors.sendEmail}</FormErrorMessage>
              </FormControl>
            </VStack>
          </Modal>
        )}
      </Formik>
    </>
  );
};

export default SendSecureLinkModalButton;
